"use client";

import React, {
  useContext,
  useState,
  useEffect,
  ChangeEvent,
  FormEvent,
} from "react";
import { useModal } from "../../hooks/useModal";
import PageMeta from "../../components/common/PageMeta";
import PageBreadcrumb from "../../components/common/PageBreadCrumb";
import Input from "../../components/form/input/InputField";
import Label from "../../components/form/Label";
import Button from "../../components/ui/button/Button";
import { Modal } from "../../components/ui/modal";
import AuthContext from "../../context/AuthContext";
import { API_USER_URL, getImageUrl } from "../../config";
import { toast } from 'react-hot-toast';
import Badge from "../../components/ui/badge/Badge";
import { Edit, Mail, User as UserIcon } from 'lucide-react';

export default function UserMetaCard() {
  const { isOpen, openModal, closeModal } = useModal();
  const { user, login: contextLogin } = useContext(AuthContext);

  // Local state for display and editing
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [profileImage, setProfileImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);

  // Function to get role display name with proper capitalization
  const getRoleDisplayName = (role: string): string => {
    if (role === "admin") return "Administrador";
    if (role === "worker") return "Trabajador";
    return role.charAt(0).toUpperCase() + role.slice(1);
  };

  // Function to get role badge color
  const getRoleBadgeColor = (role: string): "primary" | "success" | "error" | "warning" | "info" => {
    if (role === "admin") return "primary";
    if (role === "worker") return "success";
    return "info";
  };

  // On mount, load user data from context
  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setProfileImage(user.profileImage ? getImageUrl(user.profileImage) : getImageUrl());
    }
  }, [user]);

  // Handle new profile image selection and preview
  const handleImageUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validar tipo de archivo
    if (!file.type.startsWith('image/')) {
      toast.error("Por favor selecciona un archivo de imagen válido");
      return;
    }

    // Validar tamaño (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen debe ser menor a 5MB");
      return;
    }

    setSelectedImage(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImage(reader.result as string);
    };
    reader.onerror = () => {
      toast.error("Error al cargar la imagen");
    };
    reader.readAsDataURL(file);
  };

  // Handle save: make API call to update profile, then update AuthContext with the new token.
  const handleSave = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Validaciones básicas
    if (!name.trim() || !email.trim()) {
      toast.error("El nombre y email son requeridos");
      return;
    }

    // Validar formato de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error("Por favor ingresa un email válido");
      return;
    }

    const loadingToast = toast.loading("Actualizando perfil...");

    try {
      const formData = new FormData();
      formData.append("name", name);
      formData.append("email", email);
      if (selectedImage) {
        formData.append("profileImage", selectedImage);
      }

      const response = await fetch(`${API_USER_URL}/profile`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${user?.token}`,
        },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        contextLogin(data.token, true);
        toast.success("Perfil actualizado exitosamente", {
          id: loadingToast,
        });
        closeModal();
      } else {
        const errorData = await response.json();
        toast.error(errorData.message || "Error al actualizar el perfil", {
          id: loadingToast,
        });
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error de conexión al actualizar el perfil", {
        id: loadingToast,
      });
    }
  };

  return (
    <>
      {/* User Information Card */}
      <div className="p-6 border border-gray-200 rounded-2xl bg-white dark:bg-gray-800/30 dark:border-gray-700 shadow-sm">
        <div className="flex flex-col sm:flex-row items-center gap-6">
          {/* Profile Image */}
          <div className="relative">
            <div className="w-24 h-24 overflow-hidden border-4 border-white dark:border-gray-700 rounded-full shadow-md">
              <img
                src={profileImage || getImageUrl()}
                alt="Foto de usuario"
                className="object-cover w-full h-full"
              />
            </div>
            {selectedImage && (
              <div className="absolute -top-1 -right-1">
                <span className="flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-indigo-500"></span>
                </span>
              </div>
            )}
          </div>
          
          {/* User Info */}
          <div className="flex-1 text-center sm:text-left">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
              <h3 className="text-xl font-semibold text-gray-800 dark:text-white">
                {name || "Nombre de Usuario"}
              </h3>
              {user?.role && (
                <div className="text-xs font-medium">
                  <Badge color={getRoleBadgeColor(user.role)} size="sm">
                    {getRoleDisplayName(user.role)}
                  </Badge>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-center sm:justify-start gap-1.5 text-gray-600 dark:text-gray-300 mb-3">
              <Mail className="w-4 h-4" />
              <span className="text-sm">{email || "email@ejemplo.com"}</span>
            </div>
            
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Gestiona tu información personal y controla cómo otros te ven en la plataforma.
            </p>
          </div>
          
          {/* Action Button */}
          <div className="mt-4 sm:mt-0">
            <Button 
              onClick={openModal} 
              variant="outline" 
              size="sm"
              className="flex items-center gap-1.5"
            >
              <Edit className="w-4 h-4" />
              Editar perfil
            </Button>
          </div>
        </div>
      </div>

      {/* Modal for editing profile */}
      <Modal isOpen={isOpen} onClose={closeModal} title="Editar perfil">
        <form onSubmit={handleSave} className="space-y-5">
          {/* Profile Image Upload */}
          <div className="flex flex-col items-center gap-4">
            <div className="relative">
              <div className="w-32 h-32 overflow-hidden border-4 border-white dark:border-gray-700 rounded-full shadow-md">
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Foto de perfil"
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src="/images/user/owner.jpg"
                    alt="Foto de perfil por defecto"
                    className="w-full h-full object-cover"
                  />
                )}
              </div>
              <label
                htmlFor="modalProfileImage"
                className="absolute bottom-0 right-0 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-medium px-3 py-1.5 rounded-full cursor-pointer shadow-sm transition-colors duration-200"
              >
                Cambiar
              </label>
              <input
                id="modalProfileImage"
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            </div>
            
            {user?.role && (
              <div>
                <Badge color={getRoleBadgeColor(user.role)} size="sm">
                  {getRoleDisplayName(user.role)}
                </Badge>
              </div>
            )}
          </div>
          
          {/* Form Fields */}
          <div className="mt-6 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="modalName" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <UserIcon className="w-4 h-4" />
                Nombre
              </Label>
              <Input
                id="modalName"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Ingresa tu nombre"
                className="w-full"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="modalEmail" className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <Mail className="w-4 h-4" />
                Email
              </Label>
              <Input
                id="modalEmail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Ingresa tu email"
                className="w-full"
              />
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-3 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
            <Button 
              variant="outline" 
              size="sm" 
              onClick={() => {
                closeModal();
                // Resetear cambios al cerrar
                if (user) {
                  setName(user.name);
                  setEmail(user.email);
                  setProfileImage(user.profileImage ? getImageUrl(user.profileImage) : getImageUrl());
                }
                setSelectedImage(null);
              }}
              className="px-4"
            >
              Cancelar
            </Button>
            <Button 
              variant="primary" 
              size="sm"
              type="submit"
              className="px-4"
            >
              Guardar cambios
            </Button>
          </div>
        </form>
      </Modal>
    </>
  );
}